import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';

import Api from '@/api/Api';

import { useLocalDateRange } from '@/utils/useDateRangeAdapter';
import { generateSeries, pointEntryFactoryXYDay } from '@/utils/chartsUtils';
import type { ChartSeries } from '@/utils/types/chartsUtilsTypes';
import {
	buildGssChartItems,
	buildHeatmapColorSettings,
} from '@/utils/spoofingGssHeatmapUtils';
import type { GssDisplayMode, GssItem } from '@/utils/spoofingGssHeatmapUtils';
import type { HeatmapTooltipRow } from '@/utils/spoofingVisibilityHeatmapUtils';

import { datetimeToString } from '@/components/common/dateUtils';
import { SPOOFING_GSS_DATE_RANGE_LIMIT } from '@/components/stats/spoofingGssDateRangeConfig';


export const VISIBILITY_THRESHOLD_OPTIONS = Array.from({ length: 11 }, (_, index) => {
	const value = 50 + index * 5;
	return {
		value,
		label: String(value) + '%',
	};
});

export const DEFAULT_VISIBILITY_THRESHOLD = 75;

type HeatmapChartSeries = {
	name: string;
	hidden: boolean;
	data: Array<{ x: unknown; y: number | null; data: HeatmapTooltipRow[] }>;
};


const mapHeatmapSeries = (series: ChartSeries[]): HeatmapChartSeries[] => {
	return series.map(serie => ({
		name: serie.name,
		hidden: serie.hidden,
		data: serie.data.map(point => {
			if (Array.isArray(point)) {
				return { x: point[0], y: point[1], data: [] as HeatmapTooltipRow[] };
			}
			const tooltipRows = Array.isArray(point.tooltipRows)
				? point.tooltipRows as HeatmapTooltipRow[]
				: [];
			return {
				x: point.x,
				y: point.y,
				data: tooltipRows,
			};
		}),
	}));
};


export const useSpoofingGssHeatmap = (displayMode: GssDisplayMode) => {
	const { t } = useI18n();
	const { datesRange, datesRangeStr, datesRangeSafe } = useLocalDateRange(1, 0);
	const mountpointsSelected = ref<string[]>([]);
	const visibilityThreshold = ref(DEFAULT_VISIBILITY_THRESHOLD);
	const { apiData, isLoading, doRequest } = Api.useSpoofingGssGet();

	const fetchStatsItems = () => {
		if (datesRangeStr.value == null || mountpointsSelected.value.length === 0) {
			return;
		}
		doRequest({
			start_date: datesRangeStr.value[0],
			end_date: datesRangeStr.value[1],
			mountpoints: mountpointsSelected.value,
		});
	};

	onMounted(() => {
		fetchStatsItems();
	});

	const heatmapSeriesCategories = computed(() => {
		if (datesRangeSafe.value[0] == null || datesRangeSafe.value[1] == null) {
			return {
				series: [] as HeatmapChartSeries[],
				categories: [] as string[],
			};
		}
		const chartItems = buildGssChartItems(
			(apiData.value.items || []) as GssItem[],
			visibilityThreshold.value,
			displayMode,
		);
		const result = generateSeries({
			items: chartItems as unknown as Array<Record<string, unknown>>,
			getSeriesKeyFn: item => String(item.mountpoint),
			getValueFn: item => (typeof item.status === 'number' ? item.status : null),
			seriesStep: apiData.value.period || 5,
			seriesStepUnit: 'minute',
			categoryFormatter: datetimeToString,
			pointEntryFactory: pointEntryFactoryXYDay,
			showNullAsZero: false,
			minDate: new Date(datesRangeSafe.value[0]),
			maxDate: new Date(datesRangeSafe.value[1]),
			forceSeriesKeys: mountpointsSelected.value,
			roundTimeValues: true,
			pushAdditionalFields: ['tooltipRows'],
		});
		return {
			series: mapHeatmapSeries(result.series),
			categories: result.categories.map(category => String(category)),
		};
	});

	const heatmapSeries = computed(() => heatmapSeriesCategories.value.series);
	const heatmapCategories = computed(() => heatmapSeriesCategories.value.categories);

	const colorSettings = computed(() => buildHeatmapColorSettings(
		t('stats.spoofing.gssHeatmap.legend.ok'),
		t('stats.spoofing.gssHeatmap.legend.multi'),
		{},
		t('stats.spoofing.gssHeatmap.legend.single'),
		false,
		t('stats.spoofing.gssHeatmap.legend.break'),
	));

	return {
		gssDateRangeLimit: SPOOFING_GSS_DATE_RANGE_LIMIT,
		isLoading,
		datesRange,
		datesRangeStr,
		mountpointsSelected,
		visibilityThreshold,
		visibilityThresholdOptions: VISIBILITY_THRESHOLD_OPTIONS,
		fetchStatsItems,
		heatmapSeries,
		heatmapCategories,
		colorSettings,
	};
};
